import { CheckCircle2, XCircle, Pencil } from 'lucide-react';
import { cn } from '@rocco/utils/cn';

/**
 * Mobile result screen shown after a booking attempt.
 * Displays success or failure state with appropriate messaging and meeting details.
 * Provides action button to close or retry the booking.
 */
interface BookingResultMobileProps {
    success: boolean;
    message: string;
    consultantName?: string;
    formattedDate?: string;
    formattedTime?: string;
    timezoneAbbr?: string;
    onClose: () => void;
    eventId?: string;
    onCancel?: () => void;
    onReschedule?: () => void;
    showSpamHint?: boolean;
}

export const BookingResultMobile = ({
    success,
    message,
    consultantName,
    formattedDate,
    formattedTime,
    timezoneAbbr,
    onClose,
    eventId,
    onCancel,
    onReschedule,
    showSpamHint = false,
}: BookingResultMobileProps) => {
    return (
        <div className="mx-auto w-full bg-white text-[#161717]">
            <header className="bg-brick-600 px-6 py-5 text-white">
                <h1 className="text-lg leading-6 font-semibold">
                    {success ? 'Booking Confirmed' : 'Booking Failed'}
                </h1>
            </header>

            <div className="space-y-6 px-4 py-6">
                <section className="flex flex-col items-center text-center">
                    {success ? (
                        <CheckCircle2 className="text-green-600 h-16 w-16" />
                    ) : (
                        <XCircle className="text-rose-600 h-16 w-16" />
                    )}
                    <p
                        className={cn(
                            'mt-4 text-lg font-semibold',
                            success ? 'text-green-600' : 'text-rose-600',
                        )}
                    >
                        {success ? 'Success!' : 'Error'}
                    </p>
                    <p className="mt-2 text-sm text-[#4B4B4B]">{message}</p>
                    {success && showSpamHint && (
                        <div className="mt-3 text-xs text-brick-600">
                            <p className="font-semibold">
                                Didn't Receive a calendar invite?
                            </p>
                            <p>Please check your spam or junk folder.</p>
                        </div>
                    )}
                </section>

                {success &&
                    consultantName &&
                    formattedDate &&
                    formattedTime && (
                        <section className="rounded-[14px] border border-gray-200 bg-gray-50 px-4 py-5">
                            <p className="text-center text-sm text-[#4B4B4B]">
                                Your meeting with {consultantName} is scheduled
                                for:
                            </p>
                            <div className="mt-3 text-center">
                                <p className="text-lg font-semibold text-[#161717]">
                                    {formattedDate}
                                </p>
                                <p className="mt-1 text-lg font-semibold text-[#161717]">
                                    {formattedTime} {timezoneAbbr}
                                </p>
                            </div>
                        </section>
                    )}
            </div>

            <footer className="px-4 pb-6">
                <button
                    type="button"
                    onClick={onClose}
                    className="h-12 w-full rounded-full bg-brick-600 text-base font-semibold text-white transition-colors hover:bg-[#c56843]"
                >
                    {success ? 'Done' : 'Try Again'}
                </button>
                {success && eventId && (onCancel || onReschedule) && (
                    <div className="mt-3 flex items-center justify-center gap-4">
                        {onCancel && (
                            <button
                                type="button"
                                onClick={onCancel}
                                className="flex items-center gap-2 text-sm font-medium text-[#161717] transition-colors hover:text-brick-600"
                                aria-label="Cancel meeting"
                            >
                                <XCircle className="h-4 w-4" />
                                Cancel Meeting
                            </button>
                        )}
                        {onReschedule && (
                            <button
                                type="button"
                                onClick={onReschedule}
                                className="flex items-center gap-2 text-sm font-medium text-[#161717] transition-colors hover:text-brick-600"
                                aria-label="Reschedule meeting"
                            >
                                <Pencil className="h-4 w-4" />
                                Reschedule Meeting
                            </button>
                        )}
                    </div>
                )}
            </footer>
        </div>
    );
};
